import { useCallback } from 'react';
import type { Dispatch } from 'react';
import type { Notification } from '../../types';
import {
  markAsRead as markAsReadService,
  markAllAsRead as markAllAsReadService,
  deleteNotification as deleteNotificationService,
} from '../../services/notificationService';
import type { NotificationAction, NotificationState } from './types';

export function useNotificationActions(state: NotificationState, dispatch: Dispatch<NotificationAction>) {
  // Mark as read
  const markAsRead = useCallback(
    async (id: string) => {
      try {
        await markAsReadService(id);
        const notification = state.notifications.find((n) => n.id === id);
        if (notification && !notification.isRead) {
          dispatch({
            type: 'UPDATE_NOTIFICATION',
            payload: {
              ...notification,
              isRead: true,
              readAt: new Date().toISOString(),
            },
          });
          dispatch({
            type: 'SET_UNREAD_COUNT',
            payload: Math.max(0, state.unreadCount - 1),
          });
        }
      } catch (err) {
        console.error('Error marking as read:', err);
        throw err;
      }
    },
    [state.notifications, state.unreadCount]
  );

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await markAllAsReadService();
      const updated = state.notifications.map((n) => ({
        ...n,
        isRead: true,
        readAt: new Date().toISOString(),
      }));
      dispatch({ type: 'SET_NOTIFICATIONS', payload: updated });
      dispatch({ type: 'SET_UNREAD_COUNT', payload: 0 });
    } catch (err) {
      console.error('Error marking all as read:', err);
      throw err;
    }
  }, [state.notifications]);

  // Delete notification
  const deleteNotification = useCallback(async (id: string) => {
    try {
      await deleteNotificationService(id);
      dispatch({ type: 'DELETE_NOTIFICATION', payload: id });
    } catch (err) {
      console.error('Error deleting notification:', err);
      throw err;
    }
  }, []);

  // Add notification (for manual addition)
  const addNotification = useCallback((notification: Notification) => {
    dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
  }, []);

  return { markAsRead, markAllAsRead, deleteNotification, addNotification };
}
